/**
 * `RecipientCombobox` — the Transfer modal's "Person" type-ahead.
 *
 * Presentational + self-contained: it owns query text, the open/active state of its listbox, and the
 * in-flight search; it does NOT own the committed selection (the parent does, so the payload
 * normalizer can validate a COMMITTED recipient rather than whatever is typed — see
 * `slots/buildTransferPayload.ts`).
 *
 * Built from Chakra primitives on purpose: the four allow-listed `@codametrix/ui-components` atoms
 * (`RoundedButton`, `Icon`, `CMXIcon`, `LoadingSlugs`) contain no combobox, and a `Select` is the
 * wrong control for a type-ahead over a directory.
 *
 * ARIA 1.2 combobox, and the parts that are easy to get wrong (all from the design review, B4/A10):
 * DOM focus NEVER leaves the input. The active option is communicated with
 *    `aria-activedescendant`, which is cleared whenever the list closes or a selection commits, so
 *    it can never reference an unmounted row.
 * ESCAPE PRECEDENCE. With the listbox open, Escape closes ONLY the listbox and calls
 *    `stopPropagation()` so Chakra's dialog does not also close. With it closed, Escape bubbles and
 *    closes the modal. Escape never clears a committed recipient — that is what the explicit Clear
 *    button is for.
 * Tab commits nothing (it moves on; only Enter or a click commits).
 * One `role="status"` region announces loading / result count / no matches.
 *
 * Async safety: `onSearch` is debounced (`debounceMs`, default 250) and every response carries a
 * sequence number, so a slow earlier request can never overwrite a newer one's results.
 *
 * `debounceMs` is a PROP rather than a module constant specifically so tests can drive it to 0 and
 * stay on real timers: `@testing-library/user-event@13.5.0` deadlocks under `jest.useFakeTimers()`
 * in this repo (it is why `HoldAction.test.tsx`'s clock test is red on `origin/main`), so a
 * fake-timer-based debounce test is not writable here.
 */
import { Box, Flex, Input, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type { TransferRecipient } from '../../interface/transfer';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';

export interface RecipientComboboxProps {
  /** Suggestions for an EMPTY query. Never used as a local search index — see `onSearch`. */
  readonly initialRecipients?: readonly TransferRecipient[];
  /**
   * The authoritative directory search. When absent, the control shows `initialRecipients` only and
   * says so — it does NOT silently filter a stale local array and present that as a search.
   */
  readonly onSearch?: (
    term: string
  ) => Promise<readonly TransferRecipient[]>;
  readonly selected?: TransferRecipient;
  readonly onSelect: (recipient?: TransferRecipient) => void;
  readonly isDisabled?: boolean;
  readonly isInvalid?: boolean;
  /** Id of the parent's error element, wired into `aria-describedby` when invalid. */
  readonly errorId?: string;
  readonly debounceMs?: number;
  readonly inputId?: string;
}

const LISTBOX_ID = 'transfer-recipient-listbox';
const optionId = (index: number): string =>
  `transfer-recipient-option-${index}`;

/** "Kira Park" -> "KP"; single-word names fall back to one letter. Never throws on odd input. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const caseCountLabel = (count?: number): string =>
  typeof count === 'number' ? `${count} ${count === 1 ? 'case' : 'cases'}` : '';

export const RecipientCombobox: React.FC<RecipientComboboxProps> = ({
  initialRecipients,
  onSearch,
  selected,
  onSelect,
  isDisabled,
  isInvalid,
  errorId,
  debounceMs = 250,
  inputId = 'transfer-recipient-input'
}) => {
  const [text, setText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<readonly TransferRecipient[]>(
    initialRecipients ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Monotonic request id — a resolved response is applied ONLY if it is still the newest request.
  const seqRef = useRef(0);
  // `onSearch` is held in a ref so the search effect can depend on the QUERY alone. A caller that
  // passes an inline lambda would otherwise hand us a new function identity every render, re-running
  // the effect (and re-issuing the request) on each one. Today's callers memoize, but the component
  // must not depend on that.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const query = text.trim();
  const hasSearch = Boolean(onSearch);

  useEffect(() => {
    // Empty query: show the seeded suggestions, cancel any pending search.
    if (query.length === 0) {
      seqRef.current += 1;
      setResults(initialRecipients ?? []);
      setIsLoading(false);
      return undefined;
    }
    if (!hasSearch) return undefined;
    const seq = ++seqRef.current;
    setIsLoading(true);
    const handle = setTimeout(() => {
      const search = onSearchRef.current;
      if (!search) return;
      search(query)
        .then((found) => {
          if (seq !== seqRef.current) return; // stale response — a newer query superseded it
          setResults(found);
          setIsLoading(false);
          setActiveIndex(found.length > 0 ? 0 : -1);
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setResults([]);
          setIsLoading(false);
          setActiveIndex(-1);
        });
    }, debounceMs);
    return () => clearTimeout(handle);
    // `onSearch` itself is intentionally NOT a dependency (it is read through `onSearchRef`); only
    // whether a search capability EXISTS can change the behaviour here.
  }, [query, hasSearch, debounceMs, initialRecipients]);

  // Keep the active option in view without ever moving DOM focus off the input.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const el = listRef.current?.querySelector(`#${optionId(activeIndex)}`);
    (el as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const commit = (recipient: TransferRecipient): void => {
    onSelect(recipient);
    setText(recipient.name);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const clear = (): void => {
    onSelect(undefined);
    setText('');
    setResults(initialRecipients ?? []);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) => (results.length === 0 ? -1 : (i + 1) % results.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((i) =>
        results.length === 0 ? -1 : (i - 1 + results.length) % results.length
      );
      return;
    }
    if (event.key === 'Enter') {
      if (isOpen && activeIndex >= 0 && results[activeIndex]) {
        event.preventDefault();
        commit(results[activeIndex]);
      }
      return;
    }
    if (event.key === 'Escape') {
      if (isOpen) {
        // Close ONLY the listbox; the dialog must not also close on this keystroke.
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        setActiveIndex(-1);
      }
      // When already closed, let it bubble to the Chakra dialog.
    }
  };

  const status = !onSearch
    ? 'Recipient search is unavailable.'
    : isLoading
      ? 'Searching…'
      : query.length === 0
        ? ''
        : results.length === 0
          ? 'No matches'
          : `${results.length} ${results.length === 1 ? 'result' : 'results'}`;

  return (
    <Box position="relative">
      <Flex align="center" gap={2}>
        <Input
          id={inputId}
          ref={inputRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          // Chakra's `Input` computes its own `aria-invalid` from `isInvalid` and overwrites a raw
          // attribute of the same name, so the invalid state MUST be set through the Chakra prop.
          isInvalid={isInvalid}
          aria-describedby={isInvalid && errorId ? errorId : undefined}
          data-testid="transfer-recipient-input"
          autoComplete="off"
          isDisabled={isDisabled}
          placeholder="Search people…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setIsOpen(true);
            // Editing the text drops any committed selection: what is shown must never disagree
            // with what will be submitted.
            if (selected) onSelect(undefined);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          bg={TRANSFER_COLORS.inputBg}
          borderColor={
            isInvalid ? TRANSFER_COLORS.error : TRANSFER_COLORS.border
          }
          borderRadius={CASE_ACTION_RADIUS.control}
          textStyle="Body 1"
        />
        {selected && (
          <Box
            as="button"
            type="button"
            data-testid="transfer-recipient-clear"
            aria-label="Clear recipient"
            onClick={clear}
            disabled={isDisabled}
            px={2}
            py={1}
            borderRadius={CASE_ACTION_RADIUS.small}
            border={`1px solid ${TRANSFER_COLORS.border}`}
            color={TRANSFER_COLORS.inkMuted}
            textStyle="Body 1"
          >
            Clear
          </Box>
        )}
      </Flex>

      {/* One polite live region for loading / count / no-matches. */}
      <Text
        role="status"
        aria-live="polite"
        data-testid="transfer-recipient-status"
        textStyle="Body 1"
        color={TRANSFER_COLORS.inkFaint}
        mt={1}
      >
        {status}
      </Text>

      {isOpen && (
        <Box
          ref={listRef}
          id={LISTBOX_ID}
          role="listbox"
          aria-label="People"
          data-testid="transfer-recipient-listbox"
          // Overlays the form rather than reflowing it — matching the prototype, and so the Reason
          // pills below never shift under the pointer mid-interaction.
          position="absolute"
          zIndex={10}
          left={0}
          right={0}
          mt={1}
          maxH="16rem"
          overflowY="auto"
          bg="white"
          border={`1px solid ${TRANSFER_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.control}
          boxShadow="md"
        >
          <Box
            role="group"
            aria-label={`People, ${results.length}`}
            data-testid="transfer-recipient-group"
            bg={TRANSFER_COLORS.groupHeaderBg}
            px={3}
            py={1}
            textStyle="Body 1"
            color={TRANSFER_COLORS.inkMuted}
          >
            PEOPLE · {results.length}
          </Box>
          {results.length === 0 && (
            <Box px={3} py={3} textStyle="Body 1" color={TRANSFER_COLORS.inkFaint}>
              {isLoading ? 'Searching…' : 'No matches'}
            </Box>
          )}
          {results.map((recipient, index) => (
            <Flex
              key={recipient.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              data-testid={`transfer-recipient-option-${recipient.id}`}
              align="center"
              gap={3}
              px={3}
              py={2}
              cursor="pointer"
              bg={
                index === activeIndex ? TRANSFER_COLORS.optionActiveBg : 'white'
              }
              // `onMouseDown` (not `onClick`): the input must not blur before the commit lands.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(recipient);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <Flex
                aria-hidden
                align="center"
                justify="center"
                flexShrink={0}
                boxSize="2rem"
                borderRadius="full"
                bg={TRANSFER_COLORS.iconBg}
                color={TRANSFER_COLORS.iconArrow}
                textStyle="Body 1"
              >
                {initialsOf(recipient.name)}
              </Flex>
              <Box minW={0} flex="1">
                <Text textStyle="Body 1" color={TRANSFER_COLORS.ink} fontWeight={600}>
                  {recipient.name}
                </Text>
                {recipient.role && (
                  <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                    {recipient.role}
                  </Text>
                )}
              </Box>
              {typeof recipient.caseCount === 'number' && (
                <Text
                  textStyle="Body 1"
                  color={TRANSFER_COLORS.inkFaint}
                  flexShrink={0}
                >
                  {caseCountLabel(recipient.caseCount)}
                </Text>
              )}
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
};
